import { SelectOption } from 'components/shared/Select'
import { action, computed, observable } from 'mobx'
import { ActionDTO, Page } from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import { PaginationSearchBase } from 'store/PaginationSearchBase'
import { TriggerSearchFunc } from 'store/PaginationSearchBase/PaginationSearchBase.types'
import { RootStore } from 'store/RootStore'
import { Api, parseCamelCase, getStartAndEndDates } from 'utilities'
import { capitalize } from 'validate.js'
import {
  ActionHistoryFilterDTO,
  actionHistoryTableNeedTimeoutProps,
  ActionHistoryTableSearchProps,
  ActionHistoryTableSortProps,
  initialActionHistoryPaginationProps,
  initialActionHistoryTableSearchProps,
  FilterPeriods
} from './ActionHistoryTableStore.types'

export const ACTION_HISTORY_TABLE_STORE = 'ACTION_HISTORY_TABLE_STORE'

export class ActionHistoryTableStore extends PaginationSearchBase<
  ActionHistoryTableSortProps,
  ActionHistoryTableSearchProps,
  ActionDTO
  > {
  private _rootStore: RootStore

  constructor (rootStore: RootStore) {
    super(
      initialActionHistoryPaginationProps,
      initialActionHistoryTableSearchProps,
      [],
      actionHistoryTableNeedTimeoutProps
    )
    this._rootStore = rootStore
  }

  @observable userId: number = null
  @observable filterPeriod: FilterPeriods = FilterPeriods.CUSTOM
  @observable actionTypes: string[] = null

  @action setActionTypes = (actionTypes: string[]) => {
    this.actionTypes = actionTypes
  }

  @action setFilterPeriod = (filterPeriod: FilterPeriods) => {
    this.filterPeriod = filterPeriod
  }

  @action setUserId = (userId: number) => {
    this.userId = userId
  }

  onFilterPeriodChange = (filterPeriod: FilterPeriods) => {
    this.setFilterPeriod(filterPeriod)
    if (filterPeriod === FilterPeriods.CUSTOM) return
    this.onSearchPropsChange({ period: null })
  }

  triggerSearch: TriggerSearchFunc = (options) => {
    if (!this.userId) return
    const {
      action,
      changes,
      period,
      title
    } = this.searchProps
    const body: ActionHistoryFilterDTO = {
      action,
      changes,
      ...getStartAndEndDates(period, this.filterPeriod),
      title,
      userId: `U${this.userId}`
    }

    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this._rootStore[GENERAL_STORE]
    const {
      sortProp,
      sortDirection,
      page,
      size
    } = this.pagination

    setLoadingOn()
    Promise.all([
      Api.post<Page<ActionDTO>>(
        `/history/search?page=${options?.resetPageNumber ? 0 : page
        }&size=${size
        }&sort=${sortProp},${sortDirection}`,
        body,
        'actionHistoryApi'
      ),
      this.actionTypes
        ? undefined
        : Api.get<string[]>('/history/types', 'actionHistoryApi')
    ])
      .then(([data, actionTypes]) => {
        this.setSearchResults(data.content)
        this.handleSearchResultPaginationProps(data)
        actionTypes && this.setActionTypes(actionTypes)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  @computed get actionTypeOptions (): SelectOption<string>[] {
    return (this.actionTypes || []).map(action => ({
      label: capitalize(parseCamelCase(action).toLowerCase()),
      value: action
    }))
  }
}
